import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post } from "@common/api/apiShared";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { EntityType } from "@common/Common.types";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { entityKey, entityTypeLabel } from "../RiskOperationalOverview.types";
import { IAddEntitiesDialog } from "./AddEntitiesDialog";
import { AvailableRiskAreaEntity } from "./AddEntitiesDialog.types";
import { assessableEntityTypeOptions } from "../../riskFilterUtils";

export interface SelectableEntityRow {
    key: string;
    type: EntityType;
    typeLabel: string;
    name: string;
    isSelected: boolean;
}

// Mirrors the internal search of DotLegalTableWithControls (case-insensitive substring across the
// non-excluded columns name + typeLabel, with "exact" quote handling) so select-all and the header
// checkbox act only on the rows the table is actually showing.
export function entityMatchesSearch(row: SelectableEntityRow, searchString: string): boolean {
    if (!searchString) return true;

    const firstQuoteIndex = searchString.indexOf('"');
    const lastQuoteIndex = searchString.lastIndexOf('"');

    let exactString = "";
    if (firstQuoteIndex > -1 && lastQuoteIndex > -1 && firstQuoteIndex !== lastQuoteIndex) {
        exactString = searchString.substring(firstQuoteIndex + 1, lastQuoteIndex);
    }

    const needle = firstQuoteIndex === lastQuoteIndex ? searchString.slice(firstQuoteIndex + 1) : searchString;

    const searchableValues = [row.name, row.typeLabel].map((value) => value.toLowerCase());

    if (exactString.trim().length === 0) {
        return searchableValues.some((value) => value.includes(needle.toLowerCase()));
    }
    return searchableValues.some((value) => value === exactString.toLowerCase());
}

export function useAddEntitiesDialog(props: IAddEntitiesDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();

    const { isLoading, data } = useDotLegalQuery<Array<AvailableRiskAreaEntity>>(`/risk-areas/${props.riskAreaId}/entities/available`);

    const [selectedKeys, setSelectedKeys] = useState<Array<string>>([]);
    const [selectedTypes, setSelectedTypes] = useState<Array<string>>([]);
    const [searchString, setSearchString] = useState("");

    const typeOptions: Array<DotLegalSelectOption> = assessableEntityTypeOptions(translateString);

    const filteredEntities = (data ?? []).filter((entity) => selectedTypes.length === 0 || selectedTypes.includes(entity.type.toString()));

    const rows: Array<SelectableEntityRow> = filteredEntities.map((entity) => ({
        key: entityKey(entity.type, entity.id),
        type: entity.type,
        typeLabel: entityTypeLabel(entity.type, translateString),
        name: entity.name,
        isSelected: selectedKeys.includes(entityKey(entity.type, entity.id)),
    }));

    // The rows the table is actually showing — type filter (above) plus the table's search box.
    const visibleKeys = rows.filter((row) => entityMatchesSearch(row, searchString)).map((row) => row.key);

    const toggleEntity = (key: string) => {
        setSelectedKeys((current) => (current.includes(key) ? current.filter((x) => x !== key) : [...current, key]));
    };

    // Select-all acts on the visible view only, preserving selections made under other filters.
    const onSelectAllChanged = () => {
        const allVisibleSelected = visibleKeys.every((key) => selectedKeys.includes(key));
        setSelectedKeys((current) =>
            allVisibleSelected ? current.filter((key) => !visibleKeys.includes(key)) : Array.from(new Set([...current, ...visibleKeys]))
        );
    };

    const getHeaderCheckBoxState = (): "true" | "false" | "indeterminate" => {
        const noneSelected = visibleKeys.every((key) => !selectedKeys.includes(key));
        if (noneSelected) return "false";

        const allSelected = visibleKeys.every((key) => selectedKeys.includes(key));
        return allSelected ? "true" : "indeterminate";
    };

    const selectedEntities = (data ?? []).filter((entity) => selectedKeys.includes(entityKey(entity.type, entity.id)));

    const saveMutation = useDotLegalMutation(
        () =>
            post(`/risk-areas/${props.riskAreaId}/entities`, {
                entities: selectedEntities.map((entity) => ({ entityType: entity.type, entityId: entity.id })),
            }),
        {
            onSuccess: () => {
                snackbar.show(translateString("entitiesAdded"));
                props.onSaved();
                props.onClose();
            },
        }
    );

    const onSaveClick = () => {
        saveMutation.mutateAsync();
    };

    return {
        isLoading,
        rows,
        hasEntities: (data ?? []).length > 0,
        typeOptions,
        selectedTypes,
        setSelectedTypes,
        toggleEntity,
        onSelectAllChanged,
        getHeaderCheckBoxState,
        setSearchString,
        onSaveClick,
        isSaving: saveMutation.isLoading,
        hasSelection: selectedKeys.length > 0,
        selectedCount: selectedKeys.length,
    };
}
